import { useMutation } from '@src/query';


/**
 * M31 per-carrier BYOCA connection mutation hooks (#517). Each wraps the shared
 * `useMutation` engine with the injected-`queryClient`
 * `mutateFn({ apiClient, queryClient })` contract — the corelib query singleton
 * is NOT imported here. Invalidation runs inside `mutateFn` (the one place
 * `queryClient` is injected). Both mutations invalidate the per-seller carriers
 * key `['root', '', 'shipping', sellerId, 'carriers']`, which the caller passes
 * as `queryKey`.
 *
 * Mirrors `query/shipping/useShippingConnectionMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/** Connect the seller's own carrier account (Owner/Manager only). POSTs that
 * carrier's raw credential fields (see `CARRIER_FIELDS`); the backend stores
 * them encrypted and returns the non-secret status shape (never echoes
 * secrets). A missing field → 400 keyed to that field. Variables:
 * `{ sellerId, carrier, creds, queryKey }`. */
function useCarrierConnectMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, carrier, creds, queryKey }) => apiClient
      .request({
        url: `/shipping/${sellerId}/carriers/${carrier}/`,
        method: 'POST',
        payload: creds,
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Disconnect the seller's carrier account (Owner/Manager only). DELETE with no
 * body; returns the status shape with `connected=false`. Variables:
 * `{ sellerId, carrier, queryKey }`. */
function useCarrierDisconnectMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, carrier, queryKey }) => apiClient
      .request({
        url: `/shipping/${sellerId}/carriers/${carrier}/`,
        method: 'DELETE',
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


export {
  useCarrierConnectMutation,
  useCarrierDisconnectMutation,
};
